/**
 * Small helpers for onboarding and setup guidance (docs/ONBOARDING_PLAN.md).
 */
import { SETUP_DEVICES, type SetupDevice } from '../hooks/useSetup.ts';

/** Where every "Connect" call to action goes on the web. */
export const INTEGRATIONS_PATH = '/profile?tab=integrations';

export const STALE_DISMISS_KEY = 'setup_stale_dismissed_until';
export const STALE_DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

/** Whether the stale banner was dismissed less than 7 days ago. */
export function staleDismissed(now: number = Date.now()): boolean {
  try {
    const until = Number(localStorage.getItem(STALE_DISMISS_KEY));
    return Number.isFinite(until) && until > now;
  } catch {
    return false;
  }
}

/** Hide the stale banner for 7 days (best effort: storage may be blocked). */
export function dismissStale(now: number = Date.now()): void {
  try {
    localStorage.setItem(STALE_DISMISS_KEY, String(now + STALE_DISMISS_MS));
  } catch {
    // Private mode: the banner hides for this visit only.
  }
}

// Display names for ``stale.source``. Anything else (including sources the
// rules say never to name) falls back to a generic "your device".
const SOURCE_NAMES: Record<string, string> = {
  garmin: 'Garmin Connect',
  garmin_connect: 'Garmin Connect',
  intervals: 'intervals.icu',
  intervals_icu: 'intervals.icu',
  apple_health: 'Apple Health',
  health_connect: 'Health Connect',
};

/** A source's display name, or null when it should be described generically. */
export function sourceName(source: string): string | null {
  return SOURCE_NAMES[source] ?? null;
}

export const ONBOARDING_STEP_KEY = 'onboarding_step';

export function loadOnboardingStep(max: number): number {
  try {
    const step = Number(localStorage.getItem(ONBOARDING_STEP_KEY));
    return Number.isInteger(step) && step >= 0 && step <= max ? step : 0;
  } catch {
    return 0;
  }
}

export function saveOnboardingStep(step: number | null): void {
  try {
    if (step === null) localStorage.removeItem(ONBOARDING_STEP_KEY);
    else localStorage.setItem(ONBOARDING_STEP_KEY, String(step));
  } catch {
    // Storage blocked: the wizard restarts at the first step next time.
  }
}

/** Toggle one device in a selection, keeping the canonical order. */
export function toggleDevice(selected: readonly SetupDevice[], device: SetupDevice): SetupDevice[] {
  const next = new Set(selected);
  if (next.has(device)) next.delete(device);
  else next.add(device);
  return SETUP_DEVICES.filter((d) => next.has(d));
}
